import { useState, useEffect, useCallback } from "react";
import { api, apiError, setToken } from "@/api";

const STATUSES = ["new", "reviewing", "shortlisted", "contacted", "booked", "declined", "archived"];
const CATEGORIES = [
  "DJ", "Music artist or live performer", "Visual artist", "Photographer", "Videographer",
  "Dancer or movement artist", "Vendor", "Volunteer or production support", "Creative collaborator", "Other",
];

async function downloadCsv(kind) {
  const { data, headers } = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ankhara-${kind}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportLink({ kind, testId }) {
  return (
    <button type="button" className="adm-export" onClick={() => downloadCsv(kind).catch(() => {})}
      data-testid={testId}>
      EXPORT CSV ↓
    </button>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      onLogin(data);
    } catch (err) {
      setError(apiError(err, "Login failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adm-login" data-testid="admin-login">
      <h1 className="adm-logo">ANKHARA</h1>
      <p className="adm-sub">ORGANIZER ACCESS</p>
      <form onSubmit={submit} className="ank-form">
        <div className="ank-field">
          <label htmlFor="adm-email">EMAIL</label>
          <input id="adm-email" data-testid="admin-email-input" type="email" required
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="ank-field">
          <label htmlFor="adm-pass">PASSWORD</label>
          <input id="adm-pass" data-testid="admin-password-input" type="password" required
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <p className="ank-form-error" data-testid="admin-login-error">{error}</p>}
        <button type="submit" className="ank-submit" disabled={busy} data-testid="admin-login-btn">
          {busy ? "…" : "ENTER"}
        </button>
      </form>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    const { data } = await api.get("/admin/applications", { params });
    setApps(data);
  }, [category, status]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const patch = async (id, body) => {
    setSaving(true);
    try {
      await api.patch(`/admin/applications/${id}`, body);
      await load();
      setOpen((o) => (o ? { ...o, ...body } : o));
    } catch {} finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-applications">
      <div className="adm-filters">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-filter-category">
          <option value="">ALL CATEGORIES</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status">
          <option value="">ALL STATUSES</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
        <ExportLink kind="applications" testId="admin-export-applications" />
      </div>
      {apps.length === 0 && <p className="adm-empty">No applications yet.</p>}
      <ul className="adm-list">
        {apps.map((a) => (
          <li key={a.id} className="adm-item">
            <button type="button" className="adm-item-head" onClick={() => setOpen(open?.id === a.id ? null : a)}
              data-testid={`admin-app-${a.id}`}>
              <span>{a.full_name}{a.artist_name ? ` — ${a.artist_name}` : ""}</span>
              <span className="adm-tags">
                <em>{a.category}</em>
                <em className={`adm-status s-${a.status}`}>{a.status.toUpperCase()}</em>
                <em>{(a.created_at || "").slice(0, 10)}</em>
              </span>
            </button>
            {open?.id === a.id && (
              <div className="adm-item-body" data-testid="admin-app-detail">
                <p><strong>Email:</strong> {a.email} · <strong>Phone:</strong> {a.phone || "—"} · <strong>City:</strong> {a.city_state}</p>
                {a.social_url && <p><strong>Social:</strong> <a href={a.social_url} target="_blank" rel="noopener noreferrer">{a.social_url}</a></p>}
                {a.portfolio_url && <p><strong>Portfolio:</strong> <a href={a.portfolio_url} target="_blank" rel="noopener noreferrer">{a.portfolio_url}</a></p>}
                {a.bio && <p><strong>Bio:</strong> {a.bio}</p>}
                {Object.entries(a.details || {}).filter(([, v]) => v).map(([k, v]) => (
                  <p key={k}><strong>{k.replace(/_/g, " ")}:</strong> {/^https?:\/\//.test(v)
                    ? <a href={v} target="_blank" rel="noopener noreferrer">{v}</a> : v}</p>
                ))}
                <p><strong>Why ANKHARA:</strong> {a.why}</p>
                <p><strong>Discovered via:</strong> {a.discover} · <strong>Retain:</strong> {a.retain_ok ? "yes" : "no"} · <strong>Email list:</strong> {a.join_list ? "yes" : "no"}</p>
                <div className="adm-edit">
                  <select value={open.status} onChange={(e) => patch(a.id, { status: e.target.value })}
                    data-testid="admin-status-select" disabled={saving}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                  <textarea rows={3} placeholder="Internal notes…" value={open.internal_notes || ""}
                    onChange={(e) => setOpen({ ...open, internal_notes: e.target.value })}
                    data-testid="admin-notes-input" />
                  <button type="button" className="ank-submit" disabled={saving}
                    onClick={() => patch(a.id, { internal_notes: open.internal_notes })}
                    data-testid="admin-notes-save-btn">{saving ? "SAVING…" : "SAVE NOTES"}</button>
                  <button type="button" className="adm-danger"
                    onClick={async () => {
                      if (!window.confirm(`Delete the application from ${a.full_name}?`)) return;
                      await api.delete(`/admin/applications/${a.id}`).catch(() => {});
                      setOpen(null);
                      await load();
                    }}
                    data-testid="admin-app-delete-btn">DELETE APPLICATION</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [addForm, setAddForm] = useState({ email: "", first_name: "", city: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/subscribers");
    setSubs(data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/subscribers", addForm);
      setAddForm({ email: "", first_name: "", city: "" });
      await load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.email} from the list?`)) return;
    await api.delete(`/admin/subscribers/${s.id}`).catch(() => {});
    await load();
  };

  const isNew = (s) =>
    s.created_at && Date.now() - new Date(s.created_at).getTime() < 14 * 24 * 3600 * 1000;

  return (
    <section data-testid="admin-subscribers">
      <form className="adm-addrow" onSubmit={add} data-testid="admin-add-subscriber">
        <input type="email" required placeholder="Email *" value={addForm.email}
          onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
          data-testid="admin-add-email" />
        <input type="text" placeholder="First name" value={addForm.first_name}
          onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
          data-testid="admin-add-name" />
        <input type="text" placeholder="City" value={addForm.city}
          onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
          data-testid="admin-add-city" />
        <button type="submit" className="ank-ghost-btn adm-add-btn" disabled={busy}
          data-testid="admin-add-btn">
          {busy ? "ADDING…" : "+ ADD"}
        </button>
        <ExportLink kind="subscribers" testId="admin-export-subscribers" />
      </form>
      <p className="adm-empty">Only add people who asked to join — manual adds skip the welcome email.</p>
      {error && <p className="ank-form-error" data-testid="admin-add-error">{error}</p>}
      {subs.length === 0 && <p className="adm-empty">No subscribers yet.</p>}
      <ul className="adm-list">
        {subs.map((s) => (
          <li key={s.id} className="adm-item adm-row" data-testid={`admin-sub-${s.id}`}>
            <span className="adm-sub-main">
              {s.email}
              {isNew(s) && <em className="adm-badge adm-badge-new">NEW</em>}
              {s.unsubscribed && <em className="adm-badge adm-badge-never">UNSUBSCRIBED</em>}
            </span>
            <span className="adm-tags">
              <em>{s.first_name || "—"}</em>
              <em>{s.city || "—"}</em>
              <em>{(s.interests || []).join(", ") || "—"}</em>
              <em>{s.source}</em>
              <em>joined {(s.created_at || "").slice(0, 10)}</em>
              {s.emails_sent > 0 ? (
                <em className="adm-badge adm-badge-sent" title={`Last: ${s.last_subject || ""}`}>
                  EMAILED ×{s.emails_sent} · {(s.last_emailed_at || "").slice(0, 10)}
                </em>
              ) : (
                <em className="adm-badge adm-badge-never">NEVER EMAILED</em>
              )}
              <em className="adm-badge" title="Approximate — some mail clients block or pre-fetch tracking">
                OPENS ×{s.opens || 0}
              </em>
              <button type="button" className="adm-danger" onClick={() => remove(s)}
                data-testid={`admin-sub-del-${s.id}`}>
                DELETE
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Inquiries() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/inquiries");
    setItems(data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const remove = async (q) => {
    if (!window.confirm(`Delete the inquiry from ${q.name}?`)) return;
    await api.delete(`/admin/inquiries/${q.id}`).catch(() => {});
    await load();
  };

  return (
    <section data-testid="admin-inquiries">
      <div className="adm-filters">
        <ExportLink kind="inquiries" testId="admin-export-inquiries" />
      </div>
      {items.length === 0 && <p className="adm-empty">No inquiries yet.</p>}
      <ul className="adm-list">
        {items.map((q) => (
          <li key={q.id} className="adm-item adm-inq" data-testid={`admin-inq-${q.id}`}>
            <p><strong>{q.name}</strong> — {q.inquiry_type} · <a href={`mailto:${q.email}`}>{q.email}</a> · {(q.created_at || "").slice(0, 10)}
              <button type="button" className="adm-danger adm-danger-inline" onClick={() => remove(q)}
                data-testid={`admin-inq-del-${q.id}`}>DELETE</button>
            </p>
            {q.organization && <p><strong>Org:</strong> {q.organization}</p>}
            <p>{q.message}</p>
            {q.link && <p><a href={q.link} target="_blank" rel="noopener noreferrer">{q.link}</a></p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function EventSettings() {
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaved(false);
    setError("");
    try {
      await api.put("/admin/settings", {
        applications_open: settings.applications_open,
        lineup_announced: settings.lineup_announced || false,
        next_session_on: settings.next_session_on || false,
        event: settings.event,
        event2: settings.event2 || {},
      });
      setSaved(true);
    } catch (err) {
      setError(apiError(err));
    }
  };

  if (!settings) return <p className="adm-empty">Loading…</p>;
  const ev = settings.event;

  return (
    <section data-testid="admin-event-settings">
      <form className="ank-form" onSubmit={save}>
        {[["title", "EVENT TITLE"], ["date", "DATE"], ["time", "TIME"], ["venue", "VENUE"],
          ["age", "AGE RESTRICTION"], ["entry", "ENTRY"], ["blurb", "SHORT BLURB"],
          ["startIso", "EVENT START (ISO — E.G. 2026-11-04T22:00:00-05:00)"],
          ["endIso", "EVENT END (ISO — ROLLS PAST MIDNIGHT, E.G. 2026-11-05T04:00:00-05:00)"],
          ["lineup", "LINEUP"], ["access", "ACCESSIBILITY INFO"], ["rsvpUrl", "RSVP LINK"],
          ["rsvpLabel", "RSVP BUTTON TEXT"], ["rsvp_count", "RSVP COUNT (MANUAL — SHOWS AS 'N GOING' WHEN SET)"],
          ["nextRsvpLabel", "NEXT EVENT BUTTON LABEL"], ["nextRsvpUrl", "NEXT EVENT BUTTON URL (LEAVE EMPTY TO HIDE THE BUTTON)"],
          ["nextSession", "NEXT SESSION TEASER (E.G. SESSIONS: 002 — DATE TBA)"]].map(([k, label]) => (
          <div className="ank-field" key={k}>
            <label htmlFor={`ev-${k}`}>{label}</label>
            <input id={`ev-${k}`} data-testid={`admin-event-${k}`} value={ev[k] || ""}
              onChange={(e) => setSettings({ ...settings, event: { ...ev, [k]: e.target.value } })} />
          </div>
        ))}
        <p style={{ fontWeight: 700, letterSpacing: "0.12em", fontSize: "0.7rem", margin: "1.2rem 0 0.2rem", textTransform: "uppercase" }}>
          Session 005 tab (homepage) — its RSVP link is the NEXT EVENT BUTTON URL above; clear that URL to hide the tab
        </p>
        {[["title", "S005 — EVENT TITLE"], ["date", "S005 — DATE"], ["time", "S005 — TIME"],
          ["startIso", "S005 — START (ISO)"], ["endIso", "S005 — END (ISO)"],
          ["venue", "S005 — VENUE"], ["lineup", "S005 — LINEUP (DJ NAMES)"], ["blurb", "S005 — SHORT BLURB"]].map(([k, label]) => (
          <div className="ank-field" key={`ev2-${k}`}>
            <label htmlFor={`ev2-${k}`}>{label}</label>
            <input id={`ev2-${k}`} data-testid={`admin-event2-${k}`} value={(settings.event2 || {})[k] || ""}
              onChange={(e) => setSettings({ ...settings, event2: { ...(settings.event2 || {}), [k]: e.target.value } })} />
          </div>
        ))}
        <label className="ank-check" data-testid="admin-apps-open">
          <input type="checkbox" checked={settings.applications_open}
            onChange={(e) => setSettings({ ...settings, applications_open: e.target.checked })} />
          <span>Applications open (uncheck to pause event-specific open calls — the archive stays open)</span>
        </label>
        <label className="ank-check" data-testid="admin-lineup-announced">
          <input type="checkbox" checked={settings.lineup_announced || false}
            onChange={(e) => setSettings({ ...settings, lineup_announced: e.target.checked })} />
          <span>Lineup announced — tease the DJ names from the LINEUP field on the homepage</span>
        </label>
        <label className="ank-check" data-testid="admin-next-session-on">
          <input type="checkbox" checked={settings.next_session_on || false}
            onChange={(e) => setSettings({ ...settings, next_session_on: e.target.checked })} />
          <span>Next session teaser — show the NEXT SESSION TEASER line on the homepage</span>
        </label>
        {error && <p className="ank-form-error">{error}</p>}
        {saved && <p className="adm-saved" data-testid="admin-settings-saved">Saved — live on the site.</p>}
        <button type="submit" className="ank-submit" data-testid="admin-settings-save-btn">SAVE SETTINGS</button>
      </form>
    </section>
  );
}

function Blast() {
  const [subject, setSubject] = useState("ANKHARA SESSIONS: 002 — YOU'RE INVITED");
  const [message, setMessage] = useState(
    "Session 002 is here. Friday, October 2 at Pianos NYC, Main Bar — four DJs, four hours, one room. Come early and stay for the progression.\n\nFree RSVP, 21+. Bring your people."
  );
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/admin/broadcasts");
    setHistory(data);
  }, []);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setCount(r.data.subscribers)).catch(() => {});
    loadHistory().catch(() => {});
  }, [loadHistory]);

  const send = async () => {
    if (!window.confirm(`Send this email to ${count ?? "all"} subscribers? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/admin/broadcast", { subject, message });
      setResult(data);
      loadHistory().catch(() => {});
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-blast">
      <div className="ank-form">
        <p className="adm-empty">
          Sends to {count === null ? "…" : count} subscriber{count === 1 ? "" : "s"}. The current event details and
          RSVP button are automatically added below your message, with the ANKHARA logo header.
        </p>
        <div className="ank-field">
          <label htmlFor="blast-subject">SUBJECT</label>
          <input id="blast-subject" data-testid="blast-subject" value={subject}
            onChange={(e) => setSubject(e.target.value)} maxLength={140} />
        </div>
        <div className="ank-field">
          <label htmlFor="blast-message">MESSAGE</label>
          <textarea id="blast-message" data-testid="blast-message" rows={7} value={message}
            onChange={(e) => setMessage(e.target.value)} />
        </div>
        {error && <p className="ank-form-error" data-testid="blast-error">{error}</p>}
        {result && (
          <p className="adm-saved" data-testid="blast-result">
            Sent to {result.sent} of {result.total} subscribers.
            {result.failed.length > 0 && ` Failed: ${result.failed.join(", ")}`}
          </p>
        )}
        <button type="button" className="ank-submit" onClick={send}
          disabled={busy || !subject.trim() || !message.trim()} data-testid="blast-send-btn">
          {busy ? "SENDING…" : "SEND TRANSMISSION"}
        </button>
      </div>

      {history.length > 0 && (
        <div className="adm-history" data-testid="blast-history">
          <p className="ank-step-label" style={{ color: "var(--ank-acid)" }}>TRANSMISSION HISTORY</p>
          <ul className="adm-list">
            {history.map((b) => {
              const rate = b.sent > 0 ? Math.round(((b.opened || 0) / b.sent) * 100) : 0;
              return (
                <li key={b.id} className="adm-item adm-row" data-testid={`blast-${b.id}`}>
                  <span className="adm-sub-main">{b.subject}</span>
                  <span className="adm-tags">
                    <em>{(b.created_at || "").slice(0, 10)}</em>
                    <em className="adm-badge adm-badge-sent">SENT {b.sent}</em>
                    {b.failed?.length > 0 && (
                      <em className="adm-badge adm-badge-never">FAILED {b.failed.length}</em>
                    )}
                    <em className="adm-badge" title="Approximate — pixel tracking">OPENED {b.opened || 0} ({rate}%)</em>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function ArchiveAdmin() {
  const [session, setSession] = useState("002");
  const [queue, setQueue] = useState([]); // {file, name, status: pending|uploading|done|failed|duplicate, error}
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkSession, setBulkSession] = useState("001");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [albumUrl, setAlbumUrl] = useState("");
  const [importSession, setImportSession] = useState("002");
  const [importLimit, setImportLimit] = useState("");
  const [importJob, setImportJob] = useState(null);
  const [importMsg, setImportMsg] = useState("");
  const [stats, setStats] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [cmpJob, setCmpJob] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/photos/stats");
      setStats(data);
    } catch { /* noop */ }
  }, []);

  const startImport = async (e) => {
    e.preventDefault();
    setImportMsg("");
    try {
      const { data } = await api.post("/admin/import-album", {
        album_url: albumUrl,
        session: importSession,
        limit: importLimit ? parseInt(importLimit, 10) : undefined,
      });
      setImportJob(data);
    } catch (e2) {
      setImportMsg(apiError(e2));
    }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/photos");
    setPhotos(data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // poll a running import job
  useEffect(() => {
    if (!importJob || importJob.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/import-jobs/${importJob.id}`);
        setImportJob(data);
        if (data.status !== "running") { load(); loadStats(); }
      } catch { /* noop */ }
    }, 3000);
    return () => clearInterval(t);
  }, [importJob, load]);

  const onPick = async (e) => {
    setError("");
    const picked = Array.from(e.target.files || []);
    const seen = new Set();
    const items = [];
    for (const f of picked) {
      if (seen.has(f.name)) continue;
      seen.add(f.name);
      items.push({ file: f, name: f.name, status: "pending", error: "" });
    }
    try {
      const { data } = await api.get("/photos", { params: { session } });
      const existing = new Set(data.map((p) => p.original_name).filter(Boolean));
      items.forEach((it) => {
        if (existing.has(it.name)) it.status = "duplicate";
      });
    } catch { /* proceed without pre-check; backend still guards */ }
    setQueue(items);
  };

  const runQueue = async (onlyFailed = false) => {
    setUploading(true);
    setError("");
    const targets = queue.filter((q) =>
      onlyFailed ? q.status === "failed" : q.status === "pending"
    );
    let cursor = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const it = targets[cursor];
        cursor += 1;
        setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "uploading" } : x)));
        try {
          const fd = new FormData();
          fd.append("session", session);
          fd.append("files", it.file);
          const { data } = await api.post("/admin/photos", fd);
          if ((data.skipped || []).includes(it.name)) {
            setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "duplicate" } : x)));
          } else if ((data.failed || []).length) {
            const reason = data.failed[0]?.reason || "Rejected by server";
            setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "failed", error: reason } : x)));
          } else {
            setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "done" } : x)));
          }
        } catch (err) {
          const msg = err?.response?.status === 413 ? "File too large for one request" : apiError(err);
          setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "failed", error: msg } : x)));
        }
      }
    };
    // four workers at a time — big batches move fast, each request stays small
    await Promise.all([worker(), worker(), worker(), worker()]);
    setUploading(false);
    await load();
  };

  const active = queue.filter((q) => q.status !== "duplicate");
  const finished = queue.filter((q) => q.status === "done" || q.status === "failed").length;
  const failedCount = queue.filter((q) => q.status === "failed").length;
  const pendingCount = queue.filter((q) => q.status === "pending").length;

  return (
    <section data-testid="admin-archive">
      {stats && (
        <div className="adm-stats" data-testid="archive-stats">
          <span className="adm-stats-total" data-testid="stats-total">TOTAL {stats.total}</span>
          {stats.by_session.map((s) => (
            <span key={s.session} className="adm-stats-chip" data-testid={`stats-session-${s.session}`}>
              SESSION {s.session}: {s.count}
            </span>
          ))}
          {stats.unassigned > 0 && (
            <span className="adm-stats-chip" data-testid="stats-unassigned">UNASSIGNED: {stats.unassigned}</span>
          )}
        </div>
      )}
      <div className="adm-panel" data-testid="dup-panel">
        <h3 className="adm-panel-title">DUPLICATES</h3>
        <div className="adm-addrow">
          <button type="button" className="ank-ghost-btn adm-mini-btn"
            disabled={dupBusy || importJob?.status === "running"}
            data-testid="find-duplicates-btn"
            onClick={async () => {
              setDupBusy(true);
              setImportMsg("");
              try {
                const { data } = await api.post("/admin/photos/duplicates/scan");
                setImportJob(data);
              } catch (e2) {
                setImportMsg(apiError(e2));
              } finally {
                setDupBusy(false);
              }
            }}>
            {dupBusy ? "SCANNING…" : "FIND DUPLICATES"}
          </button>
          {importJob?.kind === "dup-scan" && (importJob.missing_fingerprints || 0) > 0 && (
            <button type="button" className="ank-ghost-btn adm-mini-btn"
              disabled={importJob?.status === "running"}
              data-testid="fingerprint-backfill-btn"
              onClick={async () => {
                setImportMsg("");
                try {
                  const { data } = await api.post("/admin/photos/fingerprints/backfill");
                  setImportJob(data);
                } catch (e2) {
                  setImportMsg(apiError(e2));
                }
              }}>
              VERIFY WITH FINGERPRINTS ({importJob.missing_fingerprints} TO PROCESS)
            </button>
          )}
        </div>
        {importJob?.kind === "fingerprint-backfill" && importJob.status === "running" && (
          <div data-testid="backfill-progress">
            <p className="adm-links-msg">Fingerprinting {importJob.done} / {importJob.total || "?"} — slow and gentle so the site stays fast</p>
            <div className="adm-bar"><div className="adm-bar-fill"
              style={{ width: `${importJob.total ? Math.round((importJob.done / importJob.total) * 100) : 0}%` }} /></div>
          </div>
        )}
        {importJob?.kind === "fingerprint-backfill" && importJob.status === "done" && (
          <p className="adm-links-msg" data-testid="backfill-done">
            FINGERPRINTS COMPLETE — {importJob.done} processed{importJob.failed > 0 ? `, ${importJob.failed} failed (rerun to retry)` : ""}. Run FIND DUPLICATES again for the verified result.
          </p>
        )}
        {importJob?.kind === "dup-scan" && importJob.status === "done" && (
          <div data-testid="dup-preview">
            <p className="adm-links-msg" data-testid="dup-summary">
              {importJob.extras === 0
                ? "NO DUPLICATES FOUND — every photo is unique."
                : `${importJob.groups.length} DUPLICATE GROUP${importJob.groups.length === 1 ? "" : "S"} — ${importJob.extras} extra cop${importJob.extras === 1 ? "y" : "ies"} would be removed (keeps the best copy: has thumbnail, earliest upload).`}
              {(importJob.needs_fingerprint || 0) > 0 && ` ${importJob.needs_fingerprint} photo${importJob.needs_fingerprint === 1 ? "" : "s"} share a filename but can't be confirmed without fingerprints — run VERIFY WITH FINGERPRINTS first.`}
              {(importJob.missing_fingerprints || 0) > 0 && (importJob.needs_fingerprint || 0) === 0 && ` ${importJob.missing_fingerprints} photos lack fingerprints — VERIFY WITH FINGERPRINTS makes future scans instant and hash-exact.`}
            </p>
            {importJob.extras > 0 && (
              <>
                <ul className="adm-list adm-dup-list" data-testid="dup-groups">
                  {importJob.groups.map((g, i) => (
                    <li key={i} className="adm-item" data-testid={`dup-group-${i}`}>
                      <div className="adm-row">
                        <span className="adm-sub-main">
                          {g.name} — {g.count} copies ({Math.round((g.size_bytes || 0) / 1024)} KB each)
                        </span>
                        <span className="adm-tags">
                          <em className="adm-badge adm-badge-new">{g.confidence}</em>
                          <em className="adm-badge adm-badge-sent">KEEP SESSION {g.keep_session || "?"}</em>
                          <em>remove {g.remove_ids.length}</em>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <button type="button" className="adm-danger" style={{ marginTop: "0.6rem" }}
                  data-testid="dup-delete-btn"
                  onClick={async () => {
                    if (!window.confirm(`Delete ${importJob.extras} duplicate copies? Only extras are removed — the kept copy in each group is never touched.`)) return;
                    try {
                      const { data } = await api.post("/admin/photos/duplicates/delete", { job_id: importJob.id });
                      setStats(data.stats);
                      setImportMsg(`Deleted ${data.deleted} duplicates. SESSION 002 now has ${data.stats.by_session.find((s) => s.session === "002")?.count || 0} photos (Lightroom album has 687).`);
                      setImportJob(null);
                      await load();
                    } catch (e2) {
                      setImportMsg(apiError(e2));
                    }
                  }}>
                  DELETE {importJob.extras} DUPLICATES
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="adm-panel" data-testid="import-panel">
        <h3 className="adm-panel-title">IMPORT FROM LIGHTROOM ALBUM</h3>
        <form className="ank-form" onSubmit={startImport} data-testid="import-form">
          <div className="adm-addrow">
            <input type="text" required placeholder="adobe.ly or lightroom.adobe.com/shares link" value={albumUrl}
              onChange={(e) => setAlbumUrl(e.target.value)} data-testid="import-url-input" />
            <input type="text" required placeholder="Session" value={importSession}
              onChange={(e) => setImportSession(e.target.value)} data-testid="import-session-input" />
            <input type="number" min="1" placeholder="Limit (test)" value={importLimit}
              onChange={(e) => setImportLimit(e.target.value)} data-testid="import-limit-input" style={{ maxWidth: 110 }} />
            <button type="submit" className="ank-submit adm-mini-go"
              disabled={importJob?.status === "running"} data-testid="import-start-btn">
              {importJob?.status === "running" ? "IMPORTING…" : "START IMPORT"}
            </button>
          </div>
        </form>
        {importMsg && <p className="ank-form-error" data-testid="import-error">{importMsg}</p>}
        <div className="adm-addrow" style={{ marginTop: "0.5rem" }}>
          <button type="button" className="ank-ghost-btn adm-mini-btn"
            disabled={cmpBusy || importJob?.status === "running"}
            data-testid="compare-album-btn"
            onClick={async () => {
              setCmpBusy(true);
              setImportMsg("");
              setCmpJob(null);
              try {
                const { data } = await api.post("/admin/album/compare", { album_url: albumUrl, session: importSession });
                setCmpJob(data);
              } catch (e2) {
                setImportMsg(apiError(e2));
              } finally {
                setCmpBusy(false);
              }
            }}>
            {cmpBusy ? "COMPARING…" : "COMPARE ALBUM TO ARCHIVE"}
          </button>
        </div>
        {cmpJob && (
          <div data-testid="compare-report">
            <p className="adm-links-msg" data-testid="compare-summary">
              {cmpJob.album_name} — album has {cmpJob.album_total}, archive session {cmpJob.session} has {cmpJob.stored_total}:
              <b> {cmpJob.missing_total} missing</b>, {cmpJob.strays_total} stored but not in album.
            </p>
            {(cmpJob.missing_total || 0) > 0 && (
              <>
                <ul className="adm-import-errors adm-dup-list" data-testid="compare-missing">
                  {cmpJob.missing.slice(0, 40).map((m, i) => (
                    <li key={i} data-testid={`compare-missing-${i}`}>
                      {m.name}{m.seen_in_failed_imports ? " — was in a failed import (never landed)" : ""}
                    </li>
                  ))}
                  {cmpJob.missing_total > 40 && <li>…and {cmpJob.missing_total - 40} more</li>}
                </ul>
                <button type="button" className="ank-submit adm-mini-go"
                  disabled={importJob?.status === "running"}
                  data-testid="import-missing-btn"
                  onClick={async () => {
                    setImportMsg("");
                    try {
                      const { data } = await api.post(`/admin/import-jobs/${cmpJob.id}/import-missing`);
                      setImportJob(data);
                    } catch (e2) {
                      setImportMsg(apiError(e2));
                    }
                  }}>
                  IMPORT {cmpJob.missing_total} MISSING ONLY
                </button>
              </>
            )}
            {(cmpJob.strays_total || 0) > 0 && (
              <ul className="adm-import-errors adm-dup-list" data-testid="compare-strays">
                {cmpJob.strays.slice(0, 20).map((s, i) => (
                  <li key={i} data-testid={`compare-stray-${i}`}>{s.name} — stored in session {cmpJob.session}, not in album (leftover test or manual upload?)</li>
                ))}
                {cmpJob.strays_total > 20 && <li>…and {cmpJob.strays_total - 20} more</li>}
              </ul>
            )}
            <p className="adm-empty">Nothing is imported or deleted until you press the button above. Strays are never auto-removed.</p>
          </div>
        )}
        <div className="adm-addrow" style={{ marginTop: "0.5rem" }}>
          <button type="button" className="ank-ghost-btn adm-mini-btn"
            disabled={importJob?.status === "running"}
            data-testid="regen-thumbs-btn"
            onClick={async () => {
              if (!window.confirm("Rebuild thumbnails + display images for every archive photo from the stored originals?")) return;
              setImportMsg("");
              try {
                const { data } = await api.post("/admin/photos/regenerate-thumbs");
                setImportJob(data);
              } catch (e2) {
                setImportMsg(apiError(e2));
              }
            }}>
            REGENERATE THUMBNAILS
          </button>
          {importJob && importJob.status !== "running" && (importJob.failed_assets?.length || 0) > 0 && (
            <button type="button" className="ank-ghost-btn adm-mini-btn"
              data-testid="import-retry-failed-btn"
              onClick={async () => {
                setImportMsg("");
                try {
                  const { data } = await api.post(`/admin/import-jobs/${importJob.id}/retry-failed`);
                  setImportJob(data);
                } catch (e2) {
                  setImportMsg(apiError(e2));
                }
              }}>
              RETRY FAILED ONLY ({importJob.failed_assets.length})
            </button>
          )}
        </div>
        {importJob && (!importJob.kind || importJob.kind.startsWith("album-import")) && (
          <div data-testid="import-progress-wrap">
            <p className="adm-links-msg" data-testid="import-progress">
              {importJob.album_name ? `${importJob.album_name} — ` : ""}
              {importJob.status === "running" && `Importing ${importJob.done + importJob.failed + importJob.skipped} / ${importJob.total || "?"} (${importJob.done} saved, ${importJob.skipped} duplicates, ${importJob.failed} failed)`}
              {importJob.status === "done" && `DONE — ${importJob.done} imported, ${importJob.skipped} duplicates skipped, ${importJob.failed} failed`}
              {importJob.status === "failed" && `FAILED — ${importJob.error || "unknown error"}`}
              {importJob.status === "interrupted" && "INTERRUPTED — server restarted mid-import; just run it again, duplicates are skipped"}
            </p>
            {(importJob.errors?.length || 0) > 0 && (
              <ul className="adm-import-errors" data-testid="import-errors">
                {importJob.errors.slice(-6).map((err, i) => (
                  <li key={i} data-testid={`import-error-${i}`}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <form className="ank-form" onSubmit={(e) => { e.preventDefault(); runQueue(false); }} data-testid="admin-upload-form">
        <div className="adm-addrow">
          <input type="text" required placeholder="Session (e.g. 002)" value={session}
            onChange={(e) => setSession(e.target.value)} data-testid="admin-photo-session" />
          <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,.cr2,.cr3,.dng,.tif,.tiff"
            onChange={onPick} data-testid="admin-photo-files" disabled={uploading} />
          <button type="submit" className="ank-ghost-btn adm-add-btn"
            disabled={uploading || pendingCount === 0}
            data-testid="admin-upload-btn">
            {uploading ? `UPLOADING…` : `UPLOAD ${pendingCount || ""} PHOTO${pendingCount === 1 ? "" : "S"}`}
          </button>
        </div>
        <p className="adm-empty">
          Files upload four at a time to stay under server limits. jpg / png / webp / heic / camera RAW, max 20MB each (60MB for RAW).
          Duplicates already in SESSION {session} are skipped by filename.
        </p>
        {error && <p className="ank-form-error" data-testid="admin-upload-error">{error}</p>}

        {queue.length > 0 && (
          <div className="adm-queue" data-testid="upload-queue">
            {uploading && (
              <p className="adm-progress" data-testid="upload-progress">
                Uploading {Math.min(finished + 1, active.length)} of {active.length}
              </p>
            )}
            {!uploading && finished > 0 && (
              <p className="adm-saved" data-testid="upload-summary">
                Done: {queue.filter((q) => q.status === "done").length} uploaded
                {failedCount > 0 && ` · ${failedCount} failed`}
                {queue.filter((q) => q.status === "duplicate").length > 0 &&
                  ` · ${queue.filter((q) => q.status === "duplicate").length} duplicates skipped`}
              </p>
            )}
            <ul className="adm-queue-list">
              {queue.map((q) => (
                <li key={q.name} data-testid={`queue-${q.name}`}>
                  <span className="adm-queue-name">{q.name}</span>
                  <em className={`adm-badge adm-q-${q.status}`}>
                    {q.status === "failed" ? `FAILED — ${q.error}` : q.status.toUpperCase()}
                  </em>
                </li>
              ))}
            </ul>
            {failedCount > 0 && !uploading && (
              <button type="button" className="ank-ghost-btn adm-add-btn" onClick={() => runQueue(true)}
                data-testid="retry-failed-btn">
                RETRY FAILED ({failedCount})
              </button>
            )}
          </div>
        )}
      </form>
      {photos.length === 0 ? (
        <p className="adm-empty">No photos uploaded yet — the archive shows "Photos dropping soon".</p>
      ) : (
        <>
          <div className="adm-bulkbar" data-testid="bulk-move-bar">
            <label className="ank-check adm-bulk-check">
              <input type="checkbox"
                checked={selected.size === photos.length && photos.length > 0}
                onChange={(e) => setSelected(e.target.checked ? new Set(photos.map((p) => p.id)) : new Set())}
                data-testid="bulk-select-all" />
              <span>{selected.size ? `${selected.size} SELECTED` : "SELECT ALL"}</span>
            </label>
            <span className="adm-move-label">MOVE TO:</span>
            <input type="text" value={bulkSession} onChange={(e) => setBulkSession(e.target.value)}
              placeholder="session" data-testid="bulk-move-session" />
            <button type="button" className="ank-ghost-btn adm-mini-btn"
              disabled={bulkBusy || selected.size === 0}
              data-testid="bulk-move-btn"
              onClick={async () => {
                setBulkBusy(true);
                setBulkMsg("");
                try {
                  const { data } = await api.post("/admin/photos/bulk-move", {
                    ids: Array.from(selected),
                    session: bulkSession || undefined,
                  });
                  setBulkMsg(`Moved ${data.moved}${data.failed?.length ? ` · ${data.failed.length} skipped (filename exists in destination)` : ""}`);
                  setSelected(new Set());
                  await load();
                } catch (e2) {
                  setBulkMsg(apiError(e2));
                } finally {
                  setBulkBusy(false);
                }
              }}>
              {bulkBusy ? "MOVING…" : `MOVE SELECTED (${selected.size})`}
            </button>
            <button type="button" className="ank-ghost-btn adm-mini-btn"
              disabled={bulkBusy || selected.size === 0}
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear-btn">CLEAR</button>
            <button type="button" className="adm-danger"
              disabled={bulkBusy || selected.size === 0}
              data-testid="bulk-delete-btn"
              onClick={async () => {
                if (!window.confirm(`Delete ${selected.size} selected photo${selected.size === 1 ? "" : "s"}? This permanently removes the originals, thumbnails and records.`)) return;
                setBulkBusy(true);
                setBulkMsg("");
                try {
                  const { data } = await api.post("/admin/photos/bulk-delete", { ids: Array.from(selected) });
                  setBulkMsg(`Deleted ${data.deleted} photo${data.deleted === 1 ? "" : "s"}`);
                  setSelected(new Set());
                  await load();
                } catch (e2) {
                  setBulkMsg(apiError(e2));
                } finally {
                  setBulkBusy(false);
                }
              }}>
              DELETE SELECTED ({selected.size})
            </button>
            {bulkMsg && <em className="adm-badge adm-badge-sent" data-testid="bulk-move-result">{bulkMsg}</em>}
          </div>
          <ul className="adm-list" style={{ marginTop: "1rem" }}>
            {photos.map((p) => (
              <PhotoRow key={p.id} p={p} onChanged={load}
                selected={selected.has(p.id)}
                onToggle={() => setSelected((s) => {
                  const n = new Set(s);
                  if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                  return n;
                })} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function PhotoRow({ p, onChanged, selected, onToggle }) {
  const [moveSession, setMoveSession] = useState(p.session);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const dirty = moveSession !== p.session;

  const move = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.patch(`/admin/photos/${p.id}`, { session: moveSession });
      await onChanged();
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const setHero = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/photos/${p.id}/hero`);
      await onChanged();
    } catch { /* noop */ } finally {
      setBusy(false);
    }
  };

  return (
    <li className="adm-item" data-testid={`admin-photo-${p.id}`}>
      <div className="adm-row">
        <span className="adm-sub-main adm-photo-cell">
          <input type="checkbox" checked={selected} onChange={onToggle}
            data-testid={`admin-photo-check-${p.id}`} aria-label="Select photo" />
          <img src={p.thumb_url} alt="" className="adm-thumb"
            onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = p.file_url; } }} />
          SESSION {p.session}{p.original_name ? ` — ${p.original_name}` : ""}
          {p.is_hero && <em className="adm-badge adm-badge-new">HERO</em>}
        </span>
        <span className="adm-tags">
          <em>downloads {p.downloads || 0}</em>
          {!p.is_hero && (
            <button type="button" className="ank-ghost-btn adm-mini-btn" disabled={busy}
              onClick={setHero} data-testid={`admin-hero-${p.id}`}>SET HERO</button>
          )}
          <button type="button" className="adm-danger" disabled={busy} onClick={async () => {
            if (!window.confirm("Delete this photo permanently?")) return;
            await api.delete(`/admin/photos/${p.id}`).catch(() => {});
            await onChanged();
          }} data-testid={`admin-photo-del-${p.id}`}>DELETE</button>
        </span>
      </div>
      <div className="adm-moverow">
        <span className="adm-move-label">MOVE TO:</span>
        <input type="text" value={moveSession} onChange={(e) => setMoveSession(e.target.value)}
          data-testid={`admin-move-session-${p.id}`} placeholder="session" />
        <button type="button" className="ank-ghost-btn adm-mini-btn" disabled={busy || !dirty}
          onClick={move} data-testid={`admin-move-btn-${p.id}`}>
          {busy ? "MOVING…" : "MOVE"}
        </button>
        {err && <em className="adm-badge adm-q-failed">{err}</em>}
      </div>
    </li>
  );
}

function CommunityAdmin() {
  const [links, setLinks] = useState([]);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [comments, setComments] = useState([]);
  const [reports, setReports] = useState([]);
  const [kind, setKind] = useState("community");
  const [linkSession, setLinkSession] = useState("002");
  const [label, setLabel] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [l, p, a, c, r] = await Promise.all([
      api.get("/admin/upload-links"),
      api.get("/admin/community", { params: { status: "pending" } }),
      api.get("/admin/community", { params: { status: "approved" } }),
      api.get("/admin/community-comments"),
      api.get("/admin/reports"),
    ]);
    setLinks(l.data);
    setPending(p.data);
    setApproved(a.data);
    setComments(c.data);
    setReports(r.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const createLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await api.post("/admin/upload-links", { kind, session: linkSession, label: label || undefined });
      setLabel("");
      setMsg("LINK CREATED");
      await load();
    } catch (e2) {
      setMsg(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (t) => {
    const url = `${window.location.origin}/upload/${t}`;
    try { navigator.clipboard.writeText(url); } catch { /* clipboard unavailable */ }
    setMsg(`COPIED ${url}`);
  };

  const approve = async (id) => {
    await api.post(`/admin/community/${id}/approve`).catch(() => {});
    await load();
  };

  const reject = async (id) => {
    if (!window.confirm("Reject and permanently delete this photo?")) return;
    await api.delete(`/admin/community/${id}`).catch(() => {});
    await load();
  };

  const delComment = async (id) => {
    await api.delete(`/admin/community-comments/${id}`).catch(() => {});
    await load();
  };

  const rxTotal = (p) => Object.values(p.reactions || {}).reduce((a, b) => a + b, 0);

  return (
    <section data-testid="admin-community">
      <div className="adm-panel" data-testid="upload-links-panel">
        <h3 className="adm-panel-title">UPLOAD LINKS</h3>
        <form className="ank-form" onSubmit={createLink} data-testid="link-create-form">
          <div className="adm-addrow">
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="link-kind-select">
              <option value="community">COMMUNITY — needs approval</option>
              <option value="photographer">PHOTOGRAPHER — live instantly</option>
            </select>
            <input type="text" required placeholder="Session (e.g. 002)" value={linkSession}
              onChange={(e) => setLinkSession(e.target.value)} data-testid="link-session-input" />
            <input type="text" placeholder="Label (optional, e.g. stewgives)" value={label}
              onChange={(e) => setLabel(e.target.value)} data-testid="link-label-input" />
            <button type="submit" className="ank-submit adm-mini-go" disabled={busy} data-testid="link-create-btn">
              {busy ? "…" : "CREATE LINK"}
            </button>
          </div>
        </form>
        {msg && <p className="adm-links-msg" data-testid="links-msg">{msg}</p>}
        <ul className="adm-list">
          {links.map((l) => (
            <li key={l.token} className="adm-item" data-testid={`link-${l.token}`}>
              <div className="adm-row">
                <span className="adm-sub-main">
                  /upload/{l.token}
                  {l.label ? ` — ${l.label}` : ""}
                </span>
                <span className="adm-tags">
                  <em className={`adm-badge ${l.kind === "photographer" ? "adm-badge-sent" : "adm-badge-new"}`}>
                    {l.kind.toUpperCase()}
                  </em>
                  <em>SESSION {l.session}</em>
                  <em>{l.uploads || 0} uploads</em>
                  {!l.active && <em className="adm-badge adm-badge-never">REVOKED</em>}
                  <button type="button" className="ank-ghost-btn adm-mini-btn"
                    onClick={() => copyLink(l.token)} data-testid={`link-copy-${l.token}`}>COPY</button>
                  {l.active && (
                    <button type="button" className="adm-danger" onClick={async () => {
                      if (!window.confirm("Revoke this link? It stops working immediately.")) return;
                      await api.post(`/admin/upload-links/${l.token}/revoke`).catch(() => {});
                      await load();
                    }} data-testid={`link-revoke-${l.token}`}>REVOKE</button>
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="adm-panel" data-testid="pending-panel">
        <h3 className="adm-panel-title">PENDING APPROVAL ({pending.length})
          {pending.length > 1 && (
            <button type="button" className="ank-ghost-btn adm-mini-btn" style={{ marginLeft: 12 }}
              data-testid="approve-all-btn"
              onClick={async () => {
                if (!window.confirm(`Approve all ${pending.length} pending uploads?`)) return;
                for (const p of pending) {
                  await api.post(`/admin/community/${p.id}/approve`).catch(() => {});
                }
                await load();
              }}>
              APPROVE ALL
            </button>
          )}
        </h3>
        {pending.length === 0 && <p className="adm-empty">Nothing in the darkroom.</p>}
        <ul className="adm-list">
          {pending.map((p) => (
            <li key={p.id} className="adm-item" data-testid={`pending-${p.id}`}>
              <div className="adm-row">
                <span className="adm-sub-main adm-photo-cell">
                  <img src={p.thumb_url} alt="" className="adm-thumb" onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = p.file_url; } }} />
                  {p.handle || "anonymous"} — SESSION {p.session}
                </span>
                <span className="adm-tags">
                  {p.media_type === "video" && <em className="adm-badge adm-badge-new">VIDEO</em>}
                  {p.consent ? <em>consent ✓</em> : <em className="adm-badge adm-badge-never">NO CONSENT</em>}
                  <em>{(p.created_at || "").slice(0, 16).replace("T", " ")}</em>
                  <button type="button" className="ank-ghost-btn adm-mini-btn"
                    onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}>APPROVE</button>
                  <button type="button" className="adm-danger"
                    onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}>REJECT</button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="adm-panel" data-testid="reports-panel">
        <h3 className="adm-panel-title">REPORTS / TAG REMOVALS ({reports.length})</h3>
        {reports.length === 0 && <p className="adm-empty">No open reports.</p>}
        <ul className="adm-list">
          {reports.map((r) => (
            <li key={r.id} className="adm-item" data-testid={`report-row-${r.id}`}>
              <div className="adm-row">
                <span className="adm-sub-main adm-photo-cell">
                  {r.photo?.thumb_url && <img src={r.photo.thumb_url} alt="" className="adm-thumb" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                  {r.tag ? `tag "${r.tag}"` : "photo"}{r.reason ? ` — ${r.reason}` : ""} · SESSION {r.photo?.session || "?"}
                </span>
                <span className="adm-tags">
                  <em>{(r.created_at || "").slice(0, 16).replace("T", " ")}</em>
                  {r.tag && (
                    <button type="button" className="ank-ghost-btn adm-mini-btn"
                      onClick={async () => { await api.post(`/admin/reports/${r.id}/remove-tag`).catch(() => {}); await load(); }}
                      data-testid={`report-removetag-${r.id}`}>REMOVE TAG</button>
                  )}
                  <button type="button" className="ank-ghost-btn adm-mini-btn"
                    onClick={async () => { await api.post(`/admin/reports/${r.id}/resolve`).catch(() => {}); await load(); }}
                    data-testid={`report-dismiss-${r.id}`}>DISMISS</button>
                  <button type="button" className="adm-danger"
                    onClick={async () => {
                      if (!window.confirm("Remove this photo from the feed permanently?")) return;
                      await api.delete(`/admin/community/${r.photo_id}`).catch(() => {});
                      await api.post(`/admin/reports/${r.id}/resolve`).catch(() => {});
                      await load();
                    }}
                    data-testid={`report-removephoto-${r.id}`}>REMOVE PHOTO</button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="adm-panel" data-testid="feed-live-panel">
        <h3 className="adm-panel-title">LIVE ON THE FEED ({approved.length})</h3>
        {approved.length === 0 && <p className="adm-empty">Nothing live yet.</p>}
        <ul className="adm-list">
          {approved.map((p) => (
            <li key={p.id} className="adm-item" data-testid={`approved-${p.id}`}>
              <div className="adm-row">
                <span className="adm-sub-main adm-photo-cell">
                  <img src={p.thumb_url} alt="" className="adm-thumb" onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = p.file_url; } }} />
                  {p.handle || "anonymous"} — SESSION {p.session}
                </span>
                <span className="adm-tags">
                  {p.source === "photographer" && <em className="adm-badge adm-badge-sent">PRO</em>}
                  {p.media_type === "video" && <em className="adm-badge adm-badge-new">VIDEO</em>}
                  {p.is_potn && <em className="adm-badge adm-badge-new">PHOTO OF THE NIGHT</em>}
                  {p.is_featured && <em className="adm-badge adm-badge-new">FEATURED</em>}
                  <em>{rxTotal(p)} reactions</em>
                  <em>{p.comments_count || 0} comments</em>
                  <button type="button" className="ank-ghost-btn adm-mini-btn"
                    onClick={async () => { await api.post(`/admin/community/${p.id}/feature`).catch(() => {}); await load(); }}
                    data-testid={`feature-${p.id}`}>
                    {p.is_featured ? "UNFEATURE" : "FEATURE"}
                  </button>
                  <button type="button" className="ank-ghost-btn adm-mini-btn"
                    onClick={async () => { await api.post(`/admin/community/${p.id}/potn`).catch(() => {}); await load(); }}
                    data-testid={`potn-${p.id}`}>
                    {p.is_potn ? "UN-POTN" : "PHOTO OF THE NIGHT"}
                  </button>
                  <button type="button" className="adm-danger"
                    onClick={() => reject(p.id)} data-testid={`remove-${p.id}`}>REMOVE</button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="adm-panel" data-testid="comments-panel">
        <h3 className="adm-panel-title">RECENT COMMENTS ({comments.length})</h3>
        {comments.length === 0 && <p className="adm-empty">No comments yet.</p>}
        <ul className="adm-list">
          {comments.map((c) => (
            <li key={c.id} className="adm-item" data-testid={`admin-cmt-${c.id}`}>
              <div className="adm-row">
                <span className="adm-sub-main"><b>{c.handle || "anonymous"}</b> — {c.text}</span>
                <span className="adm-tags">
                  <em>{(c.created_at || "").slice(0, 16).replace("T", " ")}</em>
                  <button type="button" className="adm-danger"
                    onClick={() => delComment(c.id)} data-testid={`cmt-del-${c.id}`}>DELETE</button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("applications");
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { setToken(null); setUser(null); });
  }, []);

  useEffect(() => {
    if (user) api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [user]);

  if (user === undefined) return <div className="adm-shell" />;
  if (!user) return <div className="adm-shell"><Login onLogin={setUser} /></div>;

  const tabs = [
    ["applications", `APPLICATIONS${overview ? ` (${overview.applications})` : ""}`],
    ["subscribers", `SUBSCRIBERS${overview ? ` (${overview.subscribers})` : ""}`],
    ["inquiries", `INQUIRIES${overview ? ` (${overview.inquiries})` : ""}`],
    ["blast", "EMAIL BLAST"],
    ["archive", "ARCHIVE PHOTOS"],
    ["community", `COMMUNITY${overview?.community_pending ? ` (${overview.community_pending} PENDING)` : ""}`],
    ["event", "EVENT & RSVP LINK"],
  ];

  return (
    <div className="adm-shell" data-testid="admin-dashboard">
      <header className="adm-head">
        <span className="adm-logo-sm">ANKHARA — ARCHIVE ROOM</span>
        <button type="button" className="ank-ghost-btn" data-testid="admin-logout-btn"
          onClick={async () => { await api.post("/auth/logout").catch(() => {}); setToken(null); setUser(null); }}>
          LOG OUT
        </button>
      </header>
      <nav className="adm-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" className={`ank-chip ${tab === key ? "on" : ""}`}
            onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}>
            {label}
          </button>
        ))}
      </nav>
      <main className="adm-main">
        {tab === "applications" && <Applications />}
        {tab === "subscribers" && <Subscribers />}
        {tab === "inquiries" && <Inquiries />}
        {tab === "blast" && <Blast />}
        {tab === "archive" && <ArchiveAdmin />}
        {tab === "community" && <CommunityAdmin />}
        {tab === "event" && <EventSettings />}
      </main>
    </div>
  );
}
